import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Stack,
  Paper,
  Typography,
  Chip,
  Grid,
  Card,
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Divider,
  TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useLocales } from '../../locales';
import Iconify from '../minimal/iconify/Iconify';

export const ConfigPreview = ({ config, activeDay, eventName }) => {
  const { translate } = useLocales();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedPreviewDay, setSelectedPreviewDay] = useState('tuesday');
  const [previewSlots, setPreviewSlots] = useState([]);

  const getDayNameFromDate = useCallback((date) => {
    try {
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayIndex = date && date.getDay ? date.getDay() : 2; // Default to tuesday if invalid
      return dayNames[dayIndex] || 'tuesday';
    } catch (error) {
      console.error('Error getting day name from date:', error);
      return 'tuesday';
    }
  }, []);

  // CRITICAL: Memoized weekly statistics - only recalculates when config changes
  // This prevents 7 calls to generateTimeSlots on EVERY render (was a major performance issue)
  const weeklyStats = useMemo(() => {
      const daySchedules = config?.daySchedules;
    // Return empty stats if config is invalid
    if (!daySchedules) {
      return { totalSlots: 0, totalCapacity: 0, activeDays: 0 };
    }

    let totalSlots = 0;
    let totalCapacity = 0;
    let activeDays = 0;

    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    days.forEach(dayKey => {
      if (daySchedules?.[dayKey]?.enabled) {
        const slots = generateTimeSlots(config.daySchedules[dayKey]);
        const availableSlots = slots.filter(s => !s.isBreak).length;
        totalSlots += availableSlots;
        totalCapacity += availableSlots * config.daySchedules[dayKey].slotCapacity;
        activeDays++;
      }
    });

    return { totalSlots, totalCapacity, activeDays };
  }, [config?.daySchedules]);

  useEffect(() => {
    if (!config) {
      return;
    }

    const dayName = getDayNameFromDate(selectedDate);
    setSelectedPreviewDay(dayName);
    const schedule = getScheduleForDay(config, dayName, selectedDate);
    const slots = generateTimeSlots(schedule);
    setPreviewSlots(slots);
  }, [config, selectedDate, getDayNameFromDate]);

  if (!config || !config.daySchedules) {
    return (
      <Alert severity="error">
        Invalid configuration provided to ConfigPreview
      </Alert>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Date Picker */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {translate('event_settings.preview_day')}
        </Typography>
        <Stack spacing={2}>
          <DatePicker
            label={translate('event_settings.choose_date_preview')}
            value={selectedDate}
            onChange={(newDate) => {
              try {
                setSelectedDate(newDate || new Date());
              } catch (error) {
                console.error('Error setting date:', error);
                setSelectedDate(new Date());
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                error={false}
                sx={{ maxWidth: 200 }}
              />
            )}
          />

          {/* Selected Day Indicator */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" color="text.secondary">
              {translate('event_settings.selected_day')}:
            </Typography>
            <Chip
              label={translate(`days.${selectedPreviewDay}`)}
              color={config.daySchedules[selectedPreviewDay]?.enabled ? 'success' : 'error'}
              size="small"
              icon={
                config.daySchedules[selectedPreviewDay]?.enabled
                  ? <Iconify icon="eva:checkmark-circle-outline" />
                  : <Iconify icon="eva:close-circle-outline" />
              }
            />
          </Stack>
        </Stack>
      </Paper>

      {/* Quick Day Selector */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {translate('event_settings.quick_day_selection')}
        </Typography>
        <Grid container spacing={1}>
          {[
            { value: 'monday', label: translate('days.monday') },
            { value: 'tuesday', label: translate('days.tuesday') },
            { value: 'wednesday', label: translate('days.wednesday') },
            { value: 'thursday', label: translate('days.thursday') },
            { value: 'friday', label: translate('days.friday') },
            { value: 'saturday', label: translate('days.saturday') },
            { value: 'sunday', label: translate('days.sunday') }
          ].map(day => {
            const schedule = config.daySchedules[day.value];
            const isEnabled = schedule?.enabled;
            const isSelected = selectedPreviewDay === day.value;

            return (
              <Grid item key={day.value}>
                <Chip
                  label={day.label}
                  onClick={() => {
                    // Find the next occurrence of this day
                    const today = new Date();
                    const targetDayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(day.value);
                    const currentDayIndex = today.getDay();

                    let daysToAdd = targetDayIndex - currentDayIndex;
                    if (daysToAdd < 0) {
                      daysToAdd += 7; // Move to next week
                    }
                    if (daysToAdd === 0 && today.getHours() >= 18) {
                      daysToAdd = 7; // If it's evening today, show next week's occurrence
                    }

                    const targetDate = new Date(today);
                    targetDate.setDate(today.getDate() + daysToAdd);
                    setSelectedDate(targetDate);
                  }}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  disabled={!isEnabled}
                  size="small"
                  icon={!isEnabled ? <Iconify icon="eva:close-circle-outline" /> : null}
                />
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Selected Day Preview */}
      <DayPreview
        day={selectedPreviewDay}
        date={selectedDate}
        config={config}
        slots={previewSlots}
      />

      {/* Weekly Statistics */}
      <Card sx={{ p: 2, bgcolor: 'info.lighter' }}>
        <Typography variant="h6" gutterBottom>
          {translate('event_settings.weekly_summary')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Stack alignItems="center">
              <Avatar sx={{ bgcolor: 'primary.main', mb: 1 }}>
                <Iconify icon="eva:calendar-outline" />
              </Avatar>
              <Typography variant="caption" color="text.secondary">
                {translate('event_settings.active_days')}
              </Typography>
              <Typography variant="h5">
                {weeklyStats.activeDays}/7
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={4}>
            <Stack alignItems="center">
              <Avatar sx={{ bgcolor: 'success.main', mb: 1 }}>
                <Iconify icon="eva:clock-outline" />
              </Avatar>
              <Typography variant="caption" color="text.secondary">
                {translate('event_settings.weekly_slots')}
              </Typography>
              <Typography variant="h5">
                {weeklyStats.totalSlots}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={4}>
            <Stack alignItems="center">
              <Avatar sx={{ bgcolor: 'warning.main', mb: 1 }}>
                <Iconify icon="eva:people-outline" />
              </Avatar>
              <Typography variant="caption" color="text.secondary">
                {translate('event_settings.weekly_capacity')}
              </Typography>
              <Typography variant="h5">
                {weeklyStats.totalCapacity}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {/* Comparison Table */}
      <ComparisonTable config={config} />
    </Stack>
  );
};

// Day Preview Component
function DayPreview({ day, date, config, slots }) {
  const { translate } = useLocales();
  const schedule = getScheduleForDay(config, day, date);

  if (!schedule || !schedule.enabled) {
    return (
      <Alert severity="warning">
        {translate('event_settings.day_not_available', { day: translate(`days.${day}`) })}
      </Alert>
    );
  }

  const availableSlots = slots.filter(s => !s.isBreak);
  const breakSlots = slots.filter(s => s.isBreak);

  return (
    <Card sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h6">
                {translate(`days.${day}`)} {translate('event_settings.schedule')}
              </Typography>
              {schedule.isOverride && (
                <Chip
                  size="small"
                  label="Special Date"
                  color="warning"
                  variant="filled"
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {date && date.toLocaleDateString ? date.toLocaleDateString() : 'Invalid date'}
            </Typography>
            {schedule.isOverride && schedule.reason && (
              <Typography variant="caption" color="warning.main">
                {schedule.reason}
              </Typography>
            )}
          </Stack>
          <Chip
            label={`${formatTime(schedule.startTime)} - ${formatTime(schedule.endTime)}`}
            color={schedule.isOverride ? "warning" : "primary"}
            variant="outlined"
          />
        </Stack>

        <Divider />

        {/* Time Slots Grid */}
        <Typography variant="subtitle2">
          {translate('event_settings.available_slots')}
        </Typography>

        <Grid container spacing={1}>
          {slots.map((slot, index) => (
            <Grid item xs={4} sm={3} md={2} key={index}>
              <Card
                sx={{
                  p: 1,
                  textAlign: 'center',
                  bgcolor: slot.isBreak ? 'grey.200' : 'background.paper',
                  opacity: slot.isBreak ? 0.7 : 1,
                  border: 1,
                  borderColor: slot.isBreak ? 'grey.300' : 'primary.light'
                }}
              >
                <Typography variant="caption" fontWeight="bold">
                  {slot.time}
                </Typography>
                {!slot.isBreak ? (
                  <>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {schedule.slotDuration}m
                    </Typography>
                    <Typography variant="caption" color="success.main">
                      {schedule.slotCapacity} {translate('event_settings.spots')}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="caption" display="block" color="text.secondary">
                    {slot.label}
                  </Typography>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Enhanced Day Statistics */}
        <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            {translate('event_settings.day_statistics')}
          </Typography>

          {/* Primary Stats Row */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Card sx={{ p: 1.5, textAlign: 'center', bgcolor: 'primary.lighter' }}>
                <Typography variant="h6" color="primary.main">
                  {schedule.slotDuration}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {translate('general.minutes')} / {translate('event_settings.slots')}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ p: 1.5, textAlign: 'center', bgcolor: 'success.lighter' }}>
                <Typography variant="h6" color="success.main">
                  {availableSlots.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {translate('event_settings.available_slots')}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ p: 1.5, textAlign: 'center', bgcolor: 'info.lighter' }}>
                <Typography variant="h6" color="info.main">
                  {schedule.slotCapacity}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {translate('event_settings.capacity_per_slot')}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{ p: 1.5, textAlign: 'center', bgcolor: 'warning.lighter' }}>
                <Typography variant="h6" color="warning.main">
                  {availableSlots.length * schedule.slotCapacity}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {translate('event_settings.daily_capacity')}
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Additional Details */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Typography variant="caption" fontWeight="bold">
                  {translate('event_settings.operating_hours')}:
                </Typography>
                <Typography variant="body2">
                  {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}
                  <Chip
                    size="small"
                    label={`${calculateOperatingHours(schedule.startTime, schedule.endTime)} ${translate('event_settings.hours_total')}`}
                    sx={{ ml: 1 }}
                  />
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack spacing={1}>
                <Typography variant="caption" fontWeight="bold">
                  {translate('event_settings.break_periods')}:
                </Typography>
                <Typography variant="body2">
                  {schedule.breakTimes?.length || 0} {translate('event_settings.breaks_configured')}
                  {schedule.breakTimes?.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      {schedule.breakTimes.map((breakTime, index) => (
                        <Chip
                          key={index}
                          size="small"
                          label={`${breakTime.label} (${formatTime(breakTime.startTime)}-${formatTime(breakTime.endTime)})`}
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  )}
                </Typography>
              </Stack>
            </Grid>
          </Grid>

          {/* Revenue Potential (if enabled) */}
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ bgcolor: 'info.lighter' }}>
                <Typography variant="subtitle2" gutterBottom>
                  {translate('event_settings.revenue_potential')}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2">
                    <strong>{availableSlots.length * schedule.slotCapacity}</strong> {translate('event_settings.max_bookings_per_day')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{Math.round((availableSlots.length * schedule.slotDuration) / 60 * 10) / 10}</strong> {translate('event_settings.productive_hours')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{schedule.breakTimes?.reduce((total, bt) => total + (parseInt(bt.endTime) - parseInt(bt.startTime)), 0) / 100 || 0}</strong> {translate('event_settings.break_hours')}
                  </Typography>
                </Stack>
              </Alert>
            </Grid>
          </Grid>
        </Paper>
      </Stack>
    </Card>
  );
}

// Comparison Table Component
function ComparisonTable({ config }) {
  const { translate } = useLocales();

  const days = [
    { key: 'monday', label: translate('days.monday'), multiplier: 1 },
    { key: 'tuesday', label: translate('days.tuesday'), multiplier: 1 },
    { key: 'wednesday', label: translate('days.wednesday'), multiplier: 1 },
    { key: 'thursday', label: translate('days.thursday'), multiplier: 1 },
    { key: 'friday', label: translate('days.friday'), multiplier: 1 },
    { key: 'saturday', label: translate('days.saturday'), multiplier: 1 },
    { key: 'sunday', label: translate('days.sunday'), multiplier: 1 }
  ];

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{translate('event_settings.day')}</TableCell>
            <TableCell>{translate('event_settings.status')}</TableCell>
            <TableCell>{translate('event_settings.hours')}</TableCell>
            <TableCell>{translate('event_settings.duration')}</TableCell>
            <TableCell>{translate('event_settings.capacity')}</TableCell>
            <TableCell>{translate('event_settings.slots')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {days.map(day => {
            const schedule = config.daySchedules[day.key];
            const slots = schedule?.enabled ? generateTimeSlots(schedule) : [];
            const availableSlots = slots.filter(s => !s.isBreak).length;

            return (
              <TableRow key={day.key}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {day.label}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={schedule?.enabled ? translate('general.open') : translate('general.closed')}
                    color={schedule?.enabled ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {schedule?.enabled
                    ? `${formatTime(schedule.startTime)} - ${formatTime(schedule.endTime)}`
                    : '-'}
                </TableCell>
                <TableCell>
                  {schedule?.enabled ? `${schedule.slotDuration}m` : '-'}
                </TableCell>
                <TableCell>
                  {schedule?.enabled ? schedule.slotCapacity : '-'}
                </TableCell>
                <TableCell>
                  {schedule?.enabled ? availableSlots * day.multiplier : '-'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Helper Functions
function getScheduleForDay(config, dayName, selectedDate = null) {
  if (!config || !config.daySchedules) return null;

  // Check for date overrides first if a date is provided
  if (selectedDate && config.dateOverrides && config.dateOverrides.length > 0) {
    const dateStr = selectedDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    const dateOverride = config.dateOverrides.find(override => override.date === dateStr);

    if (dateOverride) {
      // If schedule is null, the day is closed
      if (dateOverride.schedule === null) {
        return { enabled: false, reason: dateOverride.reason };
      }
      // Return the override schedule with additional metadata
      return {
        ...dateOverride.schedule,
        isOverride: true,
        reason: dateOverride.reason
      };
    }
  }

  // Fall back to regular day schedule
  return config.daySchedules[dayName];
}

function formatTime(time) {
  if (!time) return '';
  const hour = time.substring(0, 2);
  const min = time.substring(2, 4);
  return `${hour}:${min}`;
}

function calculateOperatingHours(startTime, endTime) {
  if (!startTime || !endTime) return 0;
  const startHour = parseInt(startTime.substring(0, 2));
  const startMin = parseInt(startTime.substring(2, 4));
  const endHour = parseInt(endTime.substring(0, 2));
  const endMin = parseInt(endTime.substring(2, 4));

  const startTotalMin = startHour * 60 + startMin;
  const endTotalMin = endHour * 60 + endMin;

  return Math.round((endTotalMin - startTotalMin) / 60 * 10) / 10;
}

function generateTimeSlots(schedule) {
  if (!schedule || !schedule.enabled) return [];

  // CRITICAL: Validate inputs to prevent infinite loops and browser crashes
  // Validate time format (must be exactly 4 characters like "0900")
  if (!schedule.startTime || schedule.startTime.length !== 4 ||
      !schedule.endTime || schedule.endTime.length !== 4) {
    console.warn('Invalid time format in schedule:', schedule);
    return [];
  }

  // Validate slotDuration (must be positive number)
  if (!schedule.slotDuration || schedule.slotDuration <= 0 || isNaN(schedule.slotDuration)) {
    console.warn('Invalid slotDuration:', schedule.slotDuration);
    return [];
  }

  const slots = [];
  const startHour = parseInt(schedule.startTime.substring(0, 2), 10);
  const startMin = parseInt(schedule.startTime.substring(2, 4), 10);
  const endHour = parseInt(schedule.endTime.substring(0, 2), 10);
  const endMin = parseInt(schedule.endTime.substring(2, 4), 10);

  // Validate parsed values
  if (isNaN(startHour) || isNaN(startMin) || isNaN(endHour) || isNaN(endMin)) {
    console.warn('Failed to parse time values:', schedule);
    return [];
  }

  // Validate time ranges
  if (startHour < 0 || startHour > 23 || endHour < 0 || endHour > 23 ||
      startMin < 0 || startMin > 59 || endMin < 0 || endMin > 59) {
    console.warn('Time values out of valid range:', schedule);
    return [];
  }

  let currentHour = startHour;
  let currentMin = startMin;
  let iterationCount = 0;
  const MAX_ITERATIONS = 1000; // Failsafe to prevent infinite loops

  while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
    // Failsafe: prevent infinite loops
    iterationCount++;
    if (iterationCount > MAX_ITERATIONS) {
      console.error('generateTimeSlots exceeded max iterations. Possible infinite loop prevented.');
      break;
    }

    const timeStr = `${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`;
    const currentTimeNum = currentHour * 100 + currentMin;

    // Check if it's a break time
    let isBreak = false;
    let breakLabel = '';

    if (schedule.breakTimes && Array.isArray(schedule.breakTimes)) {
      for (const bt of schedule.breakTimes) {
        if (!bt.startTime || !bt.endTime) continue;

        const breakStart = parseInt(bt.startTime.substring(0, 2), 10) * 100 +
                          parseInt(bt.startTime.substring(2, 4), 10);
        const breakEnd = parseInt(bt.endTime.substring(0, 2), 10) * 100 +
                        parseInt(bt.endTime.substring(2, 4), 10);

        if (currentTimeNum >= breakStart && currentTimeNum < breakEnd) {
          isBreak = true;
          breakLabel = bt?.label || '';
          break;
        }
      }
    }

    slots.push({
      time: timeStr,
      isBreak,
      label: breakLabel
    });

    // Increment time
    currentMin += schedule.slotDuration;
    while (currentMin >= 60) {
      currentMin -= 60;
      currentHour++;
    }

    // Additional failsafe: if currentHour exceeds 24, break
    if (currentHour > 24) {
      console.error('Invalid time increment detected. Breaking to prevent infinite loop.');
      break;
    }
  }

  return slots;
}